"use client";

import useSWRInfinite from "swr/infinite";
import useSWR from "swr";
import {
  Suspense,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Heading, Text } from "@radix-ui/themes";
import WatsonApi from "../../backends/WatsonApi";
import { useStores } from "../../stores/useStores";
import PageIndexSidebar from "./PageIndexSidebar";
import PageIndexList from "./PageIndexList";
import styles from "./PageIndexPage.module.scss";

export type PageIndexCategory = {
  id: string;
  category_name: string;
  category_bg_color?: string;
  icon_url?: string;
};

export type PageIndexItem = {
  id: string;
  title: string;
  url: string;
  type: string;
  breadcrumbs: string;
  picture?: string | null;
  categories?: PageIndexCategory[];
};

export type PageIndexResponse = {
  results: PageIndexItem[];
  count: number;
  next: string | null;
  previous: string | null;
};

export type AvailableLettersResponse = {
  data: string[];
};

interface PageIndexPageProps {
  initialDataPromise?: Promise<PageIndexResponse>;
  initialLetterPromise?: Promise<AvailableLettersResponse>;
}

function PageIndexPageContent({
  initialDataPromise,
  initialLetterPromise,
}: PageIndexPageProps) {
  const { organizationStore } = useStores();
  const organizationId = organizationStore.currentOrganization.id;
  const initialData = initialDataPromise ? use(initialDataPromise) : undefined;
  const initialLetter = initialLetterPromise
    ? use(initialLetterPromise)
    : undefined;
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const letterFilterRef = useRef<HTMLElement>(null);

  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch available letters (respects current search filter)
  const { data: lettersData } = useSWR<AvailableLettersResponse>(
    JSON.stringify({
      key: "page-index-letters",
      organizationId,
      search: debouncedSearch,
    }),
    async (key: string) => {
      const parsedKey = JSON.parse(key);
      const client = await WatsonApi();
      const params: Record<string, unknown> = {
        organization_pk: parsedKey.organizationId,
      };
      if (parsedKey.search) {
        params.search = parsedKey.search;
      }
      const response =
        await client.apis.organizations.organizations_page_index_available_letters(
          params,
        );
      return JSON.parse(response.data);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: !initialLetter,
      fallbackData:
        initialLetter && !debouncedSearch ? initialLetter : undefined,
    },
  );

  const availableLetters = useMemo(
    () => lettersData?.data ?? [],
    [lettersData],
  );

  // Clear active letter if it's no longer available (e.g. search narrowed results)
  useEffect(() => {
    if (
      activeLetter &&
      availableLetters.length > 0 &&
      !availableLetters.includes(activeLetter)
    ) {
      setActiveLetter(null);
    }
  }, [activeLetter, availableLetters]);

  const getKey = useCallback(
    (pageIndex: number, previousPageData: PageIndexResponse | null) => {
      if (previousPageData && !previousPageData.next) {
        return null;
      }
      return JSON.stringify({
        key: "page-index",
        organizationId,
        page: pageIndex + 1,
        search: debouncedSearch,
        letter: activeLetter,
      });
    },
    [organizationId, debouncedSearch, activeLetter],
  );

  const fetcher = useCallback(
    async (key: string): Promise<PageIndexResponse> => {
      const parsedKey = JSON.parse(key);
      const client = await WatsonApi();
      const params: Record<string, unknown> = {
        organization_pk: parsedKey.organizationId,
        page: parsedKey.page,
      };
      if (parsedKey.search) {
        params.search = parsedKey.search;
      }
      if (parsedKey.letter) {
        params.letter = parsedKey.letter;
      }
      const response =
        await client.apis.organizations.organizations_page_index_list(params);
      return JSON.parse(response.data);
    },
    [],
  );

  // Only use fallback data when no filters are active (initial unfiltered load)
  const hasFilters = !!debouncedSearch || !!activeLetter;

  const {
    data: pagesData,
    isLoading,
    setSize,
    size,
    isValidating,
  } = useSWRInfinite<PageIndexResponse>(getKey, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateOnMount: !initialData,
    fallbackData: initialData && !hasFilters ? [initialData] : undefined,
  });

  // Flatten all pages into a single array
  const allItems =
    pagesData?.reduce<PageIndexItem[]>((acc, page) => {
      acc.push(...page.results);
      return acc;
    }, []) ?? [];

  // Calculate if there's more to load
  const lastPage = pagesData?.[pagesData.length - 1];
  const hasMore = !!lastPage?.next;
  const isLoadingMore = isValidating && pagesData && pagesData.length < size;

  // IntersectionObserver to trigger loading more when scrolling to bottom
  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isValidating) {
          setSize(size + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isValidating, setSize, size]);

  const handleLetterClick = useCallback((letter: string) => {
    setActiveLetter((prev) => {
      if (letter === "") return null;
      return prev === letter ? null : letter;
    });
  }, []);

  return (
    <Box className={styles.container}>
      <Heading as="h1" size="7" weight="bold" mb="3">
        Page Directory
      </Heading>

      <PageIndexSidebar
        ref={letterFilterRef}
        letters={availableLetters}
        activeLetter={activeLetter}
        onLetterClick={handleLetterClick}
        disabled={isLoading}
        searchTerm={searchInput}
        onSearchChange={setSearchInput}
      />

      <Box className={styles.mainContent}>
        {isLoading && !pagesData ? (
          <Box py="6" style={{ textAlign: "center" }}>
            <Text size="4" color="gray">
              Loading pages...
            </Text>
          </Box>
        ) : allItems.length === 0 ? (
          <Box py="6" style={{ textAlign: "center" }}>
            <Text size="4" color="gray">
              No pages found.
            </Text>
          </Box>
        ) : (
          <>
            <PageIndexList items={allItems} />
            <Box ref={loadMoreRef} className={styles.loadMoreTrigger}>
              {isLoadingMore && (
                <Box py="4" style={{ textAlign: "center" }}>
                  <Text size="3" color="gray">
                    Loading more...
                  </Text>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function PageIndexPageClient(props: PageIndexPageProps) {
  return (
    <Suspense
      fallback={
        <Box className={styles.container}>
          <Heading as="h1" size="7" weight="bold" mb="3">
            Page Directory
          </Heading>
          <Box py="6" style={{ textAlign: "center" }}>
            <Text size="4" color="gray">
              Loading pages...
            </Text>
          </Box>
        </Box>
      }
    >
      <PageIndexPageContent {...props} />
    </Suspense>
  );
}
